import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LANGUAGES } from "@/hooks/useLanguage";
import { CATEGORIES, MAIN_SITE_URL } from "@/data/categories";

interface FooterProps {
  lang: string;
}

export default function Footer({ lang }: FooterProps) {
  const { t } = useTranslation();

  return (
    <footer className="bg-primary-950 text-background-50">
      <div className="w-full px-4 md:px-6 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="w-9 h-9 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center">
                <i className="ri-link text-lg"></i>
              </span>
              <span className="font-heading font-bold text-lg whitespace-nowrap">
                USFans<span className="text-primary-400">Spreadsheet</span>
              </span>
            </div>
            <p className="text-sm text-background-300 leading-relaxed">
              {t("footer.tagline")}
            </p>
          </div>

          <div>
            <h4 className="text-sm font-semibold mb-4 text-background-50">
              {t("footer.nav")}
            </h4>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link to={`/${lang}`} className="text-background-300 hover:text-background-50 transition-colors">
                  {t("nav.home")}
                </Link>
              </li>
              <li>
                <Link to={`/${lang}/guide`} className="text-background-300 hover:text-background-50 transition-colors">
                  {t("nav.guide")}
                </Link>
              </li>
              <li>
                <a
                  href={MAIN_SITE_URL}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-background-300 hover:text-background-50 transition-colors"
                >
                  USFans Spreadsheet
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold mb-4 text-background-50">
              {t("footer.resources")}
            </h4>
            <ul className="space-y-2.5 text-sm">
              {CATEGORIES.slice(0, 5).map((c) => (
                <li key={c.key}>
                  <a
                    href={c.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-background-300 hover:text-background-50 transition-colors"
                  >
                    {t(c.key)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold mb-4 text-background-50">
              {t("footer.languages")}
            </h4>
            <ul className="space-y-2.5 text-sm">
              {LANGUAGES.map((l) => (
                <li key={l.code}>
                  <Link
                    to={`/${l.code}`}
                    className="text-background-300 hover:text-background-50 transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-background-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-xs text-background-400 max-w-2xl leading-relaxed">
            {t("footer.disclaimer")}
          </p>
          <p className="text-xs text-background-400 whitespace-nowrap">
            © {new Date().getFullYear()} USFans Spreadsheet. {t("footer.rights")}
          </p>
        </div>
      </div>
    </footer>
  );
}